import React from "react"

import {
    Box,
    Button,
    Divider,
    Stack,
    Typography,
} from "@mui/material"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import SendOutlinedIcon from "@mui/icons-material/SendOutlined"
import ThumbUpOutlinedIcon from "@mui/icons-material/ThumbUpOutlined"
import { Trans } from "@lingui/macro"

import { AssetMatchesBrandStatus } from "models"

interface MatchOverviewSectionProps {
    compatibilityValue: number
    matchReasons: string[]
    matchStatusMessage: string | null
    rejectionReason: string | null
    status: AssetMatchesBrandStatus
    userIsBrand: boolean
    canContactAccountManager: boolean
    primaryAction: { label: string; onClick: () => void } | null
    canRefuse: boolean
    onOpenRefuseModal: () => void
    onOpenContactModal: () => void
    renderCompatibilityBar: (compatibility: number) => React.ReactNode
    renderCompatibilityEmoticon: (compatibility: number) => React.ReactNode
}

export const MatchOverviewSection = ({
    compatibilityValue,
    matchReasons,
    matchStatusMessage,
    rejectionReason,
    status,
    userIsBrand,
    canContactAccountManager,
    primaryAction,
    canRefuse,
    onOpenRefuseModal,
    onOpenContactModal,
    renderCompatibilityBar,
    renderCompatibilityEmoticon,
}: MatchOverviewSectionProps) => {
    return (
        <Box sx={{ backgroundColor: "#f4f6fb", border: "1px solid #d4d9e3" }}>
            <Box sx={{ backgroundColor: "#0f8b8d", color: "white", px: 3, py: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Typography variant="h5" fontWeight={600}>
                        <Trans id="match-details.hero.title">Il tuo Matching</Trans>
                    </Typography>
                    <Stack direction="row" spacing={1}>
                        {primaryAction && (
                            <Button
                                variant="contained"
                                startIcon={<ThumbUpOutlinedIcon />}
                                onClick={primaryAction.onClick}
                                sx={{
                                    textTransform: "none",
                                    backgroundColor: "rgba(255,255,255,0.22)",
                                    color: "white",
                                    "&:hover": { backgroundColor: "rgba(255,255,255,0.3)" },
                                }}
                            >
                                {primaryAction.label}
                            </Button>
                        )}
                        {canRefuse && (
                            <Button
                                variant="outlined"
                                onClick={onOpenRefuseModal}
                                sx={{
                                    textTransform: "none",
                                    borderColor: "rgba(255,255,255,0.65)",
                                    color: "white",
                                }}
                            >
                                <Trans id="md-reject-button">Rifiuta</Trans>
                            </Button>
                        )}
                    </Stack>
                </Stack>
            </Box>

            <Box sx={{ px: 4, py: 3 }}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "flex-start", md: "center" }}>
                    <Typography color="text.secondary">
                        <Trans id="match-details.compatibility">Livello di compatibilità</Trans>
                    </Typography>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Typography variant="h5" sx={{ color: "#24a148", fontWeight: 600, lineHeight: 1 }}>
                            {compatibilityValue}%
                        </Typography>
                        {renderCompatibilityBar(compatibilityValue)}
                        {renderCompatibilityEmoticon(compatibilityValue)}
                    </Stack>
                </Stack>

                <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 1.5 }}>
                        <Trans id="match-details.compatibility.details">Dettagli compatibilità</Trans>
                    </Typography>
                    <Stack spacing={1}>
                        {matchReasons.length === 0 && (
                            <Typography color="text.secondary">
                                <Trans id="match-details.compatibility.details.empty">Nessun punto disponibile.</Trans>
                            </Typography>
                        )}
                        {matchReasons.map((reason, index) => (
                            <Stack key={`match-reason-${index}`} direction="row" spacing={1} alignItems="center">
                                <CheckCircleOutlineIcon fontSize="small" sx={{ color: "#45b8ad" }} />
                                <Typography color="text.secondary">{reason}</Typography>
                            </Stack>
                        ))}
                    </Stack>
                </Box>

                {matchStatusMessage && (
                    status === AssetMatchesBrandStatus.REJECTED ? (
                        <Box
                            sx={{
                                mt: 2,
                                px: 1.5,
                                py: 1.25,
                                borderRadius: 1,
                                border: "1px solid #f3a8a8",
                                backgroundColor: "#fdecec",
                            }}
                        >
                            <Typography fontWeight={700} color="error.main">
                                {matchStatusMessage}
                            </Typography>
                            <Typography variant="body2" color="error.main">
                                <Trans id="match-details.status.rejected.reason">Motivazione</Trans>
                                {`: ${rejectionReason ?? "-"}`}
                            </Typography>
                        </Box>
                    ) : (
                        <Typography sx={{ mt: 2 }} color="text.secondary">
                            {matchStatusMessage}
                        </Typography>
                    )
                )}

                <Divider sx={{ my: 2.5 }} />

                {userIsBrand && (
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={<SendOutlinedIcon />}
                        onClick={onOpenContactModal}
                        disabled={!canContactAccountManager}
                        sx={{ textTransform: "none", backgroundColor: "#00a3e5" }}
                    >
                        <Trans id="match-details.contact">Contattaci</Trans>
                    </Button>
                )}
            </Box>
        </Box>
    )
}
